import React from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './login/AuthContext';
import RequireAuth from './login/RequireAuth';
import AppShell from './shared/AppShell';
import Icon from './components/Icon';
import Login from './login/Login';
import Content from './pages/Content';
import About from './pages/About';
import UserList from './components/UserList';
import F1Game from './f1/F1Game';
import F1Simulation from './f1/F1Simulation';
import Account from './pages/Account';
import Admin from './pages/Admin';
import F1Seasons from './pages/F1Seasons';
import PasswordReset from './login/PasswordReset';
import './App.css';
import ToolsApp from './tools/ToolsApp';
import { isToolsPortal } from './tools/config';
import ThemeToggle from './ThemeToggle';
import AppExit from './AppExit';
import './theme.css';

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: 1, refetchOnWindowFocus: false } } });
function AppearanceToggle() {
  const { token, profile, signOut, signOutError } = useAuth();
  const navigate = useNavigate();
  return <><ThemeToggle account={token ? profile : null}/>{token && <AppExit account={profile} app="f1" onSignOut={async () => { if (await signOut()) navigate('/login'); }} />}{signOutError && <span role="alert">{signOutError}</span>}</>;
}
function SessionRecovery() {
  const { sessionExpired, acknowledgeSessionExpiry } = useAuth();
  const location = useLocation(), navigate = useNavigate();
  React.useEffect(() => {
    if (sessionExpired && location.pathname !== '/login') {
      navigate('/login', { replace: true, state: { from: location.pathname + location.search, sessionExpired: true } });
    }
    if (sessionExpired) acknowledgeSessionExpiry();
  }, [sessionExpired, acknowledgeSessionExpiry, location.pathname, location.search, navigate]);
  return null;
}
function ShellLink({ href, ...props }) { return <Link to={href} {...props}/>; }
function F1Shell({ children }) {
  const { token, profile } = useAuth();
  const location = useLocation();
  return <AppShell app="f1" Icon={Icon} LinkComponent={ShellLink} signedIn={!!token} user={profile} activePath={location.pathname} actions={<AppearanceToggle/>}>{children}</AppShell>;
}
export default function App() {
  if (isToolsPortal) return <ToolsApp/>;
  return <QueryClientProvider client={queryClient}><AuthProvider><BrowserRouter>
    <SessionRecovery /><F1Shell><Routes>
        <Route path="/" element={<Content />} /><Route path="/content" element={<Navigate to="/" replace />} />
        <Route path="/login" element={<Login />} /><Route path="/users" element={<RequireAuth><UserList /></RequireAuth>} />
        <Route path="/f1" element={<F1Game />} /><Route path="/f1/seasons" element={<RequireAuth><F1Seasons /></RequireAuth>} /><Route path="/about" element={<About />} />
        <Route path="/f1/simulations" element={<RequireAuth><F1Simulation /></RequireAuth>} />
        <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
        <Route path="/admin" element={<RequireAuth><Admin /></RequireAuth>} />
        <Route path="/reset-password" element={<PasswordReset />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes></F1Shell>
  </BrowserRouter></AuthProvider></QueryClientProvider>;
}
